import type { Playlist, PropEntry, Scene, ShellComponent } from '../../../data/ShellConfig';
import { SCENE_TEMPLATES } from '../../../data/ShellConfig';
import type { StudioEntityType } from './dragTypes';

export function mergeProps(
    defaults: PropEntry[] | null | undefined,
    overrides: PropEntry[] | null | undefined
): PropEntry[] {
    const map = new Map<string, string>();
    for (const entry of defaults ?? []) {
        if (entry.key.trim()) {
            map.set(entry.key, entry.value);
        }
    }
    for (const entry of overrides ?? []) {
        if (entry.key.trim()) {
            map.set(entry.key, entry.value);
        }
    }
    return Array.from(map.entries()).map(([key, value]) => ({ key, value }));
}

export function buildComponentPreviewUrl(
    component: Pick<ShellComponent, 'url' | 'props'>,
    overrides?: PropEntry[]
): string {
    try {
        const url = new URL(component.url);
        for (const entry of mergeProps(component.props, overrides)) {
            url.searchParams.set(entry.key, entry.value);
        }
        return url.toString();
    } catch {
        return component.url;
    }
}

export function matchesStudioFilter(name: string, query: string): boolean {
    return name.toLowerCase().includes(query.trim().toLowerCase());
}

export const STUDIO_AUTO_NAME_MAX_LENGTH = 60;

/** Joins labels with ", " until adding another would exceed maxLength. */
export function buildAutoNameFromLabels(labels: string[], maxLength = STUDIO_AUTO_NAME_MAX_LENGTH): string {
    let result = '';
    for (const label of labels) {
        const trimmed = label.trim();
        if (!trimmed) continue;
        const next = result ? `${result}, ${trimmed}` : trimmed;
        if (next.length > maxLength) {
            if (!result) {
                return trimmed.slice(0, maxLength);
            }
            break;
        }
        result = next;
    }
    return result;
}

// Mirrors backend/resources/device-shell/buildComponentSrc.ts — the device shell renders these
// as native <img>/<video> (object-fit: contain) rather than an iframe, since a browser's default
// media viewer does not size itself to the panel. Duplicated here since the frontend app and the
// device-shell Vite app are separate builds that don't share code.
const STATIC_IMAGE_PATH = /\.(png|jpe?g|gif|webp|svg|avif)$/i;
const STATIC_VIDEO_PATH = /\.(mp4|webm|ogg|ogv|mov|m4v)$/i;

function urlPathname(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return '';
    try {
        return new URL(trimmed, 'https://example.invalid').pathname;
    } catch {
        return trimmed.split(/[?#]/, 1)[0] ?? '';
    }
}

/** True when the component URL is a direct image asset (not an HTML document). */
export function isStaticImageUrl(url: string): boolean {
    return STATIC_IMAGE_PATH.test(urlPathname(url));
}

/** True when the component URL is a direct video asset (not an HTML document). */
export function isStaticVideoUrl(url: string): boolean {
    return STATIC_VIDEO_PATH.test(urlPathname(url));
}

export type StudioIncompleteInfo = {
    incomplete: boolean;
    reason?: string;
};

/**
 * A Scene is incomplete when any panel is missing a Component, or when it has
 * fewer panels than its template expects (e.g. after older cascade deletes).
 */
export function sceneIncompleteInfo(scene: Scene): StudioIncompleteInfo {
    const template = SCENE_TEMPLATES.find(t => t.id === scene.templateId);
    const expected = template?.panels.length ?? 1;
    const panels = scene.panels ?? [];

    if (panels.length < expected) {
        return { incomplete: true, reason: 'Missing component(s)' };
    }
    if (panels.some(p => !p.componentId)) {
        return { incomplete: true, reason: 'Missing component(s)' };
    }
    return { incomplete: false };
}

/** A Playlist is incomplete when it has no Scenes assigned. */
export function playlistIncompleteInfo(playlist: Playlist): StudioIncompleteInfo {
    if ((playlist.playlistScenes ?? []).length === 0) {
        return { incomplete: true, reason: 'No scenes' };
    }
    return { incomplete: false };
}

export type StudioHoverTarget = {
    type: StudioEntityType;
    id: string;
};

/**
 * Entity ids to highlight while hovering a Studio browser tile: the hovered
 * tile itself plus its related Components / Scenes / Playlists.
 * Associations are derived from the list payloads already loaded for the page.
 */
export function relatedEntityIds(
    hover: StudioHoverTarget | null,
    scenes: Scene[],
    playlists: Playlist[]
): Set<string> {
    const related = new Set<string>();
    if (!hover) {
        return related;
    }

    related.add(hover.id);

    if (hover.type === 'asset') {
        // Assets are not wired into the component/scene/playlist graph for
        // hover highlighting yet — only the hovered tile itself is marked.
        return related;
    }

    if (hover.type === 'component') {
        for (const scene of scenes) {
            if ((scene.panels ?? []).some(p => p.componentId === hover.id)) {
                related.add(scene.id);
            }
        }
        return related;
    }

    if (hover.type === 'scene') {
        const scene = scenes.find(s => s.id === hover.id);
        for (const panel of scene?.panels ?? []) {
            if (panel.componentId) {
                related.add(panel.componentId);
            }
        }
        for (const playlist of playlists) {
            if ((playlist.playlistScenes ?? []).some(ps => ps.sceneId === hover.id)) {
                related.add(playlist.id);
            }
        }
        return related;
    }

    // playlist
    const playlist = playlists.find(p => p.id === hover.id);
    for (const entry of playlist?.playlistScenes ?? []) {
        related.add(entry.sceneId);
    }
    return related;
}
